import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';

import { Category } from '../router/category';
import * as fromRestrictionProfileConfigurations from './category-restriction-profile-configuration.reducer';
import {
  CategoryRestrictionProfileConfigurationsState,
  RestrictionProfileConfiguration,
} from './category-restriction-profile-configuration.state';

// ============================================================================
// STATE SELECTORS
// ============================================================================

export const selectRestrictionProfileConfigurationsState =
  createFeatureSelector<CategoryRestrictionProfileConfigurationsState>('restriction-profile-configuration');

export const selectRestrictionProfileConfigurationsIds = createSelector(
  selectRestrictionProfileConfigurationsState,
  fromRestrictionProfileConfigurations.selectRestrictionProfileConfigurationsIds
);

export const selectRestrictionProfileConfigurationsEntities = createSelector(
  selectRestrictionProfileConfigurationsState,
  fromRestrictionProfileConfigurations.selectRestrictionProfileConfigurationsEntities
);

export const selectAllRestrictionProfileConfigurations = createSelector(
  selectRestrictionProfileConfigurationsState,
  fromRestrictionProfileConfigurations.selectAllRestrictionProfileConfigurations
);

// // ============================================================================
// // GENERIC SELECTORS
// // ============================================================================

// Generic selector factory functions that work with any CategoryType
// These eliminate the need to replicate selectors for every category type

export const selectRestrictionProfileConfigurationsLinkedToCategory = (category: Category) => {
  return createSelector(
    selectAllRestrictionProfileConfigurations,
    (configurations: RestrictionProfileConfiguration[]) => {
      if (!category) {
        return [];
      }

      // Filter configurations that match both categoryId and categoryType
      // This selector only checks direct links, inheritance is handled in selectRestrictionProfileConfigurationsAppliedToCategory
      return configurations.filter(
        (configuration) =>
          configuration.categoryId === category.categoryId && configuration.categoryType === category.categoryType
      );
    }
  );
};

export const selectRestrictionProfileConfigurationsLoadedForCategory = (category: Category) => {
  return createSelector(
    selectAllRestrictionProfileConfigurations,
    (configurations: RestrictionProfileConfiguration[]) => {
      if (!category) {
        return false;
      }

      // Only check the specific category - don't check parents
      // Each category should load its own data, inheritance is handled by display selectors
      return configurations.some(
        (configuration) =>
          configuration.categoryId === category.categoryId && configuration.categoryType === category.categoryType
      );
    }
  );
};

// ============================================================================
// RESTRICTION PROFILE CONFIGURATION APPLIED TO CATEGORY SELECTORS
// ============================================================================

/**
 * Select restriction profile configurations applied to category (with inheritance).
 * Automatically handles inheritance logic for Brand -> Location -> Room/Kiosk.
 */
export const selectRestrictionProfileConfigurationsAppliedToCategory = (
  category: Category
): MemoizedSelector<object, RestrictionProfileConfiguration[]> => {
  if (category.parent) {
    return createSelector(
      selectRestrictionProfileConfigurationsAppliedToCategory(category.parent),
      selectRestrictionProfileConfigurationsLinkedToCategory(category),
      (parent, current) => Array.from(new Map([...parent, ...current].map((i) => [i.profileId, i])).values())
    );
  } else {
    return selectRestrictionProfileConfigurationsLinkedToCategory(category);
  }
};
